import React, { useEffect } from 'react';
import Cookies from 'js-cookie';
import { useRequest } from 'redux-query-react';
import consentRequest, { ICustomerConsentResponse } from 'components/Gdpr/requests/consentRequest';
import { useSelector } from 'react-redux';
import { CookieType } from 'components/Gdpr/requests/consentRequest';

type ConsentStatus = 'granted' | 'denied';

interface ConsentConfiguration {
    [key: string]: ConsentStatus;
}

interface RootState {
    entities: {
        customerConsentRequest?: ICustomerConsentResponse;
    };
}

interface DataLayerEvent {
    event: string;
    [key: string]: unknown;
}

declare global {
    interface Window {
        dataLayer: DataLayerEvent[];
        gtag: (...args: unknown[]) => void;
    }
}

const CONSENT_GRANTED: ConsentStatus = 'granted';
const CONSENT_DENIED: ConsentStatus = 'denied';

const GTAG_CONSENT_COMMAND = 'consent';
const GTAG_CONSENT_UPDATE = 'update';

const COOKIE_CODE_NECESSARY = 'necessary';
const COOKIE_CODE_PREFERENCE = 'preference';
const COOKIE_CODE_MARKETING = 'marketing';
const COOKIE_CODE_ANALYTICS = 'analytics';

const EVENT_MARKETING_CONSENT = 'marketing_consent';

const CONSENT_KEY_SECURITY_STORAGE = 'security_storage';
const CONSENT_KEY_FUNCTIONALITY_STORAGE = 'functionality_storage';
const CONSENT_KEY_PERSONALIZATION_STORAGE = 'personalization_storage';
const CONSENT_KEY_ANALYTICS_STORAGE = 'analytics_storage';
const CONSENT_KEY_AD_STORAGE = 'ad_storage';
const CONSENT_KEY_AD_USER_DATA = 'ad_user_data';
const CONSENT_KEY_AD_PERSONALIZATION = 'ad_personalization';

const COMPOSITE_COOKIE_MAPPINGS: Record<string, string[]> = {
    [COOKIE_CODE_NECESSARY]: [CONSENT_KEY_SECURITY_STORAGE],
    [COOKIE_CODE_MARKETING]: [CONSENT_KEY_AD_USER_DATA, CONSENT_KEY_AD_PERSONALIZATION, CONSENT_KEY_AD_STORAGE],
    [COOKIE_CODE_PREFERENCE]: [CONSENT_KEY_FUNCTIONALITY_STORAGE, CONSENT_KEY_PERSONALIZATION_STORAGE],
    [COOKIE_CODE_ANALYTICS]: [CONSENT_KEY_ANALYTICS_STORAGE],
};

const GDPR_SCRIPT_SELECTOR = 'script[type="text/x-magento-gdpr"]';
const GDPR_COOKIE_TOKEN_KEY = 'gdpr_cookie_token';

const mapStateToProps = (state: RootState) => {
    return {
        customerConsent: state.entities.customerConsentRequest as ICustomerConsentResponse,
    };
};

const ConsentHandler = (): JSX.Element => {
    const gdprCookieToken = Cookies.get(GDPR_COOKIE_TOKEN_KEY);
    const [{ isFinished, status }] = useRequest(consentRequest(gdprCookieToken ?? ''));
    const { customerConsent } = useSelector(mapStateToProps);

    const hasConsent = (cookieCode: CookieType): boolean => {
        if (!customerConsent?.data) {
            return false;
        }

        const consent = customerConsent.data.find((consent) => consent.code === cookieCode);

        return consent?.status ?? false;
    };

    const processGdprScripts = (): void => {
        const gdprScriptNodes = document.querySelectorAll(GDPR_SCRIPT_SELECTOR);

        gdprScriptNodes.forEach((scriptElement) => {
            const cookieCode = scriptElement.getAttribute('data-consent');

            if (cookieCode && hasConsent(cookieCode as CookieType)) {
                activateScript(scriptElement);
            }
        });
    };

    const activateScript = (scriptElement: Element): void => {
        const approvedScriptElement = document.createElement('script');
        approvedScriptElement.setAttribute('type', 'text/javascript');
        approvedScriptElement.innerHTML = scriptElement.innerHTML;

        scriptElement.after(approvedScriptElement);
        scriptElement.parentElement?.removeChild(scriptElement);
    };

    const mapConsentToGtm = (consentData: ICustomerConsentResponse): ConsentConfiguration => {
        const mappedConsent: ConsentConfiguration = {};

        if (!consentData?.data || consentData.data.length === 0) {
            return mappedConsent;
        }

        consentData.data.forEach((cookie) => {
            const consentStatus: ConsentStatus = cookie.status ? CONSENT_GRANTED : CONSENT_DENIED;
            const compositeKeys = COMPOSITE_COOKIE_MAPPINGS[cookie.code];

            if (compositeKeys) {
                compositeKeys.forEach((key) => {
                    mappedConsent[key] = consentStatus;
                });
            } else {
                mappedConsent[cookie.code] = consentStatus;
            }
        });

        return mappedConsent;
    };

    const updateGtmConsent = (consentData: ICustomerConsentResponse): void => {
        const mappedConsent = mapConsentToGtm(consentData);

        if (Object.keys(mappedConsent).length > 0 && window.gtag) {
            window.gtag(GTAG_CONSENT_COMMAND, GTAG_CONSENT_UPDATE, mappedConsent);
        }
    };

    const pushMarketingConsentToDataLayer = (consentData: ICustomerConsentResponse): void => {
        if (!window.dataLayer) {
            window.dataLayer = [];
        }

        const marketingConsent = consentData.data?.find((cookie) => cookie.code === COOKIE_CODE_MARKETING);

        if (marketingConsent?.status) {
            window.dataLayer.push({
                event: EVENT_MARKETING_CONSENT,
                [CONSENT_KEY_AD_STORAGE]: CONSENT_GRANTED,
                [CONSENT_KEY_AD_USER_DATA]: CONSENT_GRANTED,
                [CONSENT_KEY_AD_PERSONALIZATION]: CONSENT_GRANTED,
            });
        }
    };

    useEffect(() => {
        if (isFinished && status === 200 && customerConsent && !customerConsent.errors) {
            processGdprScripts();
            updateGtmConsent(customerConsent);
            pushMarketingConsentToDataLayer(customerConsent);
        }
    }, [isFinished, status]);

    return <React.Fragment />;
};

export default ConsentHandler;
